import React, { useState } from 'react';
import { Button, Table, Form, Alert, Spinner } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import {
  useGetHousesQuery,
  useUpdateHouseMutation,
  useUploadHouseImagesMutation,
} from '../../slices/housesApiSlice';

const BulkExcelEditHouseScreen = () => {
  const [rows, setRows] = useState([]);
  const [results, setResults] = useState([]);
  const [fileName, setFileName] = useState('');
  const { data } = useGetHousesQuery({ pageNumber: 1 });
  const [updateHouse, { isLoading: loadingUpdate }] = useUpdateHouseMutation();
  const [uploadHouseImages, { isLoading: loadingUpload }] = useUploadHouseImagesMutation();

  const validateRow = (row) => {
    if (!row.ID) return 'Missing House ID';
    if (!row.Name) return 'Missing Name';
    if (!row.Type) return 'Missing Type';
    if (!row.Address) return 'Missing Address';
    if (isNaN(parseFloat(row.RegularPrice))) return 'Invalid Regular Price';
    return null;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file || !data?.houses) return;

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const workbook = XLSX.read(evt.target.result, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const parsed = XLSX.utils.sheet_to_json(sheet, { defval: '' });

      const enriched = parsed.map((row, index) => {
        const match = data.houses.find((h) => h._id === row.ID);

        return {
          rowIndex: index + 2,
          matchedHouse: match,
          houseId: row.ID,
          type: row.Type,
          name: row.Name || match?.name || 'Sample House',
          bedrooms: parseInt(row.Bedrooms || match?.bedrooms || 0),
          bathrooms: parseInt(row.Bathrooms || match?.bathrooms || 0),
          parking: row.Parking === 'Yes',
          furnished: row.Furnished === 'Yes',
          address: row.Address || match?.address || 'Sample Address',
          offer: row.Offer === 'Yes',
          regularPrice: parseFloat(row.RegularPrice || match?.regularPrice || 0),
          discountedPrice: parseFloat(row.DiscountedPrice || match?.discountedPrice || 0),
          latitude: parseFloat(row.Latitude || match?.latitude || 0),
          longitude: parseFloat(row.Longitude || match?.longitude || 0),
          size: row.Size || match?.size || '',
          availability: row.Availability !== 'No',
          description: row.Description || match?.description || 'Sample description',
          amenities: row.Amenities ? row.Amenities.split(',').map((a) => a.trim()) : match?.amenities || [],
          agent: row.Agent || match?.agent || 'Sample Agent',
          images: row.Images
            ? row.Images.split(',').map((url) => url.trim()).slice(0, 10)
            : match?.images?.slice(0, 10) || [],
          error: validateRow(row),
        };
      });

      setRows(enriched);
      setResults([]);
      toast.success(`Loaded ${enriched.length} rows from ${file.name}`);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleImageUpload = async (e, index) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const formData = new FormData();
    files.forEach((file) => formData.append('images', file));

    try {
      const res = await uploadHouseImages(formData).unwrap();
      const updated = [...rows];
      const currentImages = updated[index].images || [];

      updated[index].images = [...currentImages, ...res.images].slice(0, 10);
      setRows(updated);
      toast.success('Images uploaded');
    } catch (err) {
      toast.error(err?.data?.message || err.message);
    }
  };

  const handleSubmit = async () => {
    const feedback = [];

    for (const row of rows) {
      if (row.error) {
        feedback.push({ index: row.rowIndex, status: 'Skipped', message: row.error });
        continue;
      }

      if (!row.houseId) {
        feedback.push({ index: row.rowIndex, status: 'Skipped', message: 'No matching house ID found' });
        continue;
      }

      const { rowIndex, error, matchedHouse, houseId, ...updatePayload } = row;

      try {
        await updateHouse({ houseId, ...updatePayload }).unwrap();
        feedback.push({ index: rowIndex, status: 'Success', message: `Updated ${updatePayload.name}` });
      } catch (err) {
        feedback.push({
          index: rowIndex,
          status: 'Failed',
          message: err?.data?.message || err.message,
        });
      }

      await new Promise((res) => setTimeout(res, 200)); // Optional delay
    }

    setResults(feedback);
    toast.success('House update process completed');
  };

  return (
    <div className="container mt-4">
      <h2>🛠️ Bulk Edit Houses by ID</h2>

      <Form.Group controlId="excelUpload" className="mb-3">
        <Form.Label>Upload Excel File (.xlsx)</Form.Label>
        <Form.Control type="file" accept=".xlsx" onChange={handleFileUpload} />
      </Form.Group>

      {rows.length > 0 && (
        <>
          <h5>Preview: {fileName}</h5>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Row</th>
                <th>ID</th>
                <th>Name</th>
                <th>Matched</th>
                <th>Type</th>
                <th>Address</th>
                <th>Agent</th>
                <th>Price</th>
                <th>Discount</th>
                <th>Size</th>
                <th>Images</th>
                <th>Upload Images</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx} style={{ background: row.error ? '#fff3cd' : 'inherit' }}>
                  <td>{row.rowIndex}</td>
                  <td>{row.houseId}</td>
                  <td>{row.name}</td>
                  <td>{row.matchedHouse?.name || '❌ Not Found'}</td>
                  <td>{row.type}</td>
                  <td>{row.address}</td>
                  <td>{row.agent}</td>
                  <td>{row.regularPrice}</td>
                  <td>{row.discountedPrice}</td>
                  <td>{row.size}</td>
                  <td>
                    {row.images.map((img, i) => (
                      <img key={i} src={img} alt="preview" style={{ width: '20px', height: '20px', marginRight: '5px' }} />
                    ))}
                    <div style={{ fontSize: '0.8rem' }}>{row.images.length} / 10</div>
                  </td>
                  <td>
                    <Form.Control
                      type="file"
                      multiple
                      onChange={(e) => handleImageUpload(e, idx)}
                      disabled={row.images.length >= 10}
                    />
                    {loadingUpload && <Spinner animation="border" size="sm" />}
                  </td>
                  <td>
                    {row.error
                      ? <span className="text-danger">{row.error}</span>
                      : row.houseId
                      ? <span className="text-success">✅ Ready</span>
                      : <span className="text-warning">No Match</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <Button onClick={handleSubmit} disabled={loadingUpdate}>
            {loadingUpdate ? <Spinner animation="border" size="sm" /> : 'Submit Updates'}
          </Button>
        </>
      )}

      {results.length > 0 && (
        <div className="mt-4">
          <h5>📋 Update Results</h5>
          {results.map((r, i) => (
            <Alert key={i} variant={
              r.status === 'Success' ? 'success' :
              r.status === 'Skipped' ? 'warning' :
              'danger'
            }>
              Row {r.index}: {r.status} — {r.message}
            </Alert>
          ))}
        </div>
      )}
    </div>
  );
};

export default BulkExcelEditHouseScreen;